import { PathParams, rest } from 'msw'
import {
  CreatePortfolioRequest,
  GetPortfoliosResponse,
  GetRewardsProductsResponse,
  Portfolio,
  PortfolioStatus,
  PortfolioTransportRequest,
  PortfolioTransportResponse,
} from 'types/rewards'
import mockCreatePortfolio from '../../../__mocks__/Rewards/createPortfolioResponse.json'
import mockDepositToPortfolio from '../../../__mocks__/Rewards/depositToPortfolioResponse.json'
import mockGetPortfolios from '../../../__mocks__/Rewards/getPortfoliosResponse.json'
import mockGetRewardsProducts from '../../../__mocks__/Rewards/getRewardsProductsResponse.json'
import mockWithdrawFromPortfolio from '../../../__mocks__/Rewards/withdrawFromPortfolioResponse.json'

const API_URL = process.env.API_URL ?? ''

// Track portfolio poll counts for simulating async activation
const portfolioPollCounts = new Map<string, number>()

// Helper function to reset poll counts (useful for testing)
export const resetPortfolioPollCounts = () => {
  portfolioPollCounts.clear()
}

export const createPortfolio = rest.post<CreatePortfolioRequest, PathParams, Portfolio>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    const body = await req.json<CreatePortfolioRequest>()

    const portfolioId = `portfolio-${Date.now()}`

    // Initialize poll count for this new portfolio
    portfolioPollCounts.set(portfolioId, 0)

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      reference: body.reference,
      id: portfolioId,
      status: PortfolioStatus.CREATED,
    }

    return res(ctx.status(201), ctx.json(response))
  }
)

export const getPortfolios = rest.get<GetPortfoliosResponse>(
  `${API_URL}/earn/v1/portfolios`,
  async (req, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockGetPortfolios))
  }
)

export const getPortfolio = rest.get<Portfolio>(
  `${API_URL}/earn/v1/portfolios/:portfolioId`,
  async (req, res, ctx) => {
    const { portfolioId } = req.params
    const id = portfolioId as string

    // Get current poll count for this portfolio
    const currentPollCount = portfolioPollCounts.get(id) ?? 0

    // Increment poll count
    portfolioPollCounts.set(id, currentPollCount + 1)

    // Determine status based on poll count
    // First 3 polls (0, 1, 2) return CREATED, 4th poll (3) onwards returns ACTIVE
    const status = currentPollCount < 3 ? PortfolioStatus.CREATED : PortfolioStatus.ACTIVE

    const response: Portfolio = {
      ...(mockCreatePortfolio as Portfolio),
      id,
      status,
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

export const depositToPortfolio = rest.post<
  PortfolioTransportRequest,
  PathParams,
  PortfolioTransportResponse
>(`${API_URL}/earn/v1/deposits`, async (req, res, ctx) => {
  const body = await req.json<PortfolioTransportRequest>()

  const response: PortfolioTransportResponse = {
    ...(mockDepositToPortfolio as PortfolioTransportResponse),
    reference: body.reference,
  }

  return res(ctx.status(201), ctx.json(response))
})

export const withdrawFromPortfolio = rest.post<
  PortfolioTransportRequest,
  PathParams,
  PortfolioTransportResponse
>(`${API_URL}/earn/v1/withdrawals`, async (req, res, ctx) => {
  const body = await req.json<PortfolioTransportRequest>()

  const response: PortfolioTransportResponse = {
    ...(mockWithdrawFromPortfolio as PortfolioTransportResponse),
    reference: body.reference,
  }

  return res(ctx.status(201), ctx.json(response))
})

export const getRewardsProducts = rest.get<GetRewardsProductsResponse>(
  `${API_URL}/earn/v1/earning-products`,
  async (req, res, ctx) => {
    const pageNumber = Number(req.url.searchParams.get('pageNumber')) || 0
    const pageSize = Number(req.url.searchParams.get('pageSize')) || 20
    const currency = req.url.searchParams.get('currency')

    let filteredProducts = [...(mockGetRewardsProducts as GetRewardsProductsResponse).content]

    if (currency) {
      filteredProducts = filteredProducts.filter(
        product => product.currency.toUpperCase() === currency.toUpperCase()
      )
    }

    const response: GetRewardsProductsResponse = {
      content: filteredProducts.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize),
      totalElements: filteredProducts.length,
      pageable: {
        pageNumber,
        pageSize,
      },
    }

    return res(ctx.status(200), ctx.json(response))
  }
)

const rewardsHandlers = [
  createPortfolio,
  getPortfolios,
  getPortfolio,
  depositToPortfolio,
  withdrawFromPortfolio,
  getRewardsProducts,
]

export default rewardsHandlers
